import { useCallback, useEffect, useState } from 'react';
import {
	PROVIDERS,
	ProviderStatus,
	StatuspageSummary,
	summarizeProvider,
} from '../../components/health-dashboard/provider-status';

/** Statuspage is a third party; do not let a slow one hold the panel open. */
const STATUS_TIMEOUT_MS = 8000;

/** Vendor status changes on the order of minutes, not seconds. */
const REFRESH_MS = 120000;

async function fetchSummary(url: string): Promise<StatuspageSummary | null> {
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);

	try {
		const response = await fetch(`${url}/api/v2/summary.json`, { signal: controller.signal });
		if (!response.ok) return null;
		return await response.json();
	} catch {
		return null;
	} finally {
		clearTimeout(timeout);
	}
}

export interface UseProviderStatusResult {
	providers: ProviderStatus[];
	/** epoch ms of the last completed fetch, for the "x ago" label. */
	fetchedAt: number | null;
	refresh: () => void;
}

/**
 * Reads each vendor's public Statuspage. Independent of the room polling loop:
 * these are third party endpoints on their own cadence, and a provider outage is
 * not something a 5s poll would catch sooner.
 */
export default function useProviderStatus(
	/** False stops the polling entirely, for a tab nobody is looking at. */
	enabled = true,
): UseProviderStatusResult {
	const [providers, setProviders] = useState<ProviderStatus[]>([]);
	const [fetchedAt, setFetchedAt] = useState<number | null>(null);
	const [nonce, setNonce] = useState(0);

	const refresh = useCallback(() => setNonce((count) => count + 1), []);

	useEffect(() => {
		if (!enabled) return undefined;

		let cancelled = false;

		Promise.all(PROVIDERS.map(async (definition) => {
			const summary = await fetchSummary(definition.url);
			return summarizeProvider(definition, summary);
		})).then((results) => {
			if (cancelled) return;
			setProviders(results);
			setFetchedAt(Date.now());
		});

		return () => { cancelled = true; };
	}, [nonce, enabled]);

	useEffect(() => {
		if (!enabled) return undefined;

		const timer = setInterval(refresh, REFRESH_MS);
		return () => clearInterval(timer);
	}, [refresh, enabled]);

	return { providers, fetchedAt, refresh };
}
